import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Box,
  Typography
} from '@mui/material';

interface WorkflowIdModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (id: string, workflowJson: any) => void;
  workflowId?: string;
  isEditing?: boolean;
  workflowJson?: any;
}

const WorkflowIdModal: React.FC<WorkflowIdModalProps> = ({
  open,
  onClose,
  onSave,
  workflowId = '',
  isEditing = false,
  workflowJson
}) => {
  const [category, setCategory] = useState<'user' | 'system'>('user');
  const [inputValue, setInputValue] = useState(workflowId);
  const [error, setError] = useState('');

  const handleSave = () => {
    if (!inputValue.trim()) {
      setError('Please enter a workflow ID');
      return;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(inputValue)) {
      setError('Only letters, numbers, underscores and hyphens are allowed');
      return;
    }

    setError('');
    onSave(inputValue, workflowJson);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (error) setError('');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {isEditing ? "Workflow ID" : "Enter Workflow ID"}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            label="Workflow ID"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Enter workflow ID"
            disabled={isEditing}
            error={!!error}
            helperText={error}
            fullWidth
            sx={isEditing ? { bgcolor: 'grey.100' } : {}}
          />

          {!isEditing && (
            <FormControl component="fieldset">
              <FormLabel component="legend">Workflow Category</FormLabel>
              <RadioGroup
                value={category}
                onChange={e => setCategory(e.target.value as 'user' | 'system')}
              >
                <FormControlLabel
                  value="user"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>User Workflow</Typography>
                      <Typography variant="caption" color="textSecondary">Standard workflow that can be modified or deleted</Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="system"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>System Workflow</Typography>
                      <Typography variant="caption" color="textSecondary">Protected workflow that cannot be deleted</Typography>
                    </Box>
                  }
                />
              </RadioGroup>
            </FormControl>
          )}

          {isEditing && (
            <Typography variant="body2" color="textSecondary">
              Workflow ID cannot be changed when editing an existing workflow
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorkflowIdModal;